import { memo, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBankAccountStore } from '@/stores/bankAccountStore';
import { useTransactionStore } from '@/stores/transactionStore';
import { useBankLogo } from '@/hooks/useBankLogo';
import { SEARCH_DEBOUNCE_MS, useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { TransactionDetailPanel } from '@/components/TransactionDetailPanel';
import { TransactionListFooter } from '@/components/TransactionListFooter';
import type { Transaction, TransactionSearchFilter } from '@/types';
import Icon from '../components/Icon';
import BankLogo from '../components/BankLogo';

/** What a row shows of its account: the owner's label and the bank's logo. */
interface RowAccount {
  label: string;
  bankName: string;
  code?: string;
  src?: string;
}

const UNKNOWN_ACCOUNT: RowAccount = { label: 'Unknown account', bankName: '' };

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

// Memoised: appending a page renders only its own rows, not the thousands already shown.
const TransactionRow = memo(function TransactionRow({
  transaction,
  account,
  onSelect,
}: {
  transaction: Transaction;
  account: RowAccount;
  onSelect: (transaction: Transaction) => void;
}) {
  return (
    <tr
      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
      onClick={() => onSelect(transaction)}
      data-testid={`transaction-row-${transaction.id}`}
    >
      <td className="hidden sm:table-cell px-6 py-4">
        <div className="flex items-center gap-2" title={account.bankName || undefined}>
          <BankLogo
            code={account.code}
            src={account.src}
            name={account.bankName || account.label}
            fallbackIcon="account_balance"
            className="h-6 w-6 shrink-0 rounded-full border border-slate-200 dark:border-slate-700"
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate max-w-[160px]">
            {account.label}
          </span>
        </div>
      </td>
      <td className="px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
              transaction.amount < 0
                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
            }`}
          >
            <Icon name={transaction.amount < 0 ? 'arrow_upward' : 'arrow_downward'} />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-slate-900 dark:text-white [overflow-wrap:anywhere] sm:truncate">{transaction.description}</span>
            {transaction.purposeCode && (
              <span className="text-xs text-slate-400 dark:text-slate-500">{transaction.purposeCode}</span>
            )}
            {/* Phone: the account and date columns are hidden, so they sit under the description */}
            <span className="sm:hidden text-xs text-slate-500 dark:text-slate-400 [overflow-wrap:anywhere]">
              {formatDate(transaction.transactionDate)} · {account.label}
            </span>
          </div>
        </div>
      </td>
      <td className="hidden sm:table-cell px-6 py-4 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
        {formatDate(transaction.transactionDate)}
      </td>
      <td
        className={`px-3 sm:px-6 py-3 sm:py-4 text-right font-semibold whitespace-nowrap ${
          transaction.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
        }`}
      >
        {transaction.amount > 0 ? '+' : ''}
        {formatCurrency(transaction.amount, transaction.currency)}
      </td>
      <td className="hidden sm:table-cell px-6 py-4 text-right text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
        {formatCurrency(transaction.runningBalance, transaction.currency)}
      </td>
    </tr>
  );
});

export const AllTransactionsPage = () => {
  const navigate = useNavigate();
  const logoFor = useBankLogo();
  const { accounts, fetchAccounts } = useBankAccountStore();
  const {
    transactions,
    isLoading,
    isLoadingMore,
    loadMoreError,
    hasMore,
    error,
    totalCount,
    transactionAccounts,
    fetchAllTransactions,
    loadMoreTransactions,
    setSearchFilters,
    clearError,
    reset: resetTransactions,
  } = useTransactionStore();

  // Filter states
  const [searchString, setSearchString] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showAmountFilter, setShowAmountFilter] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Load accounts and transactions on mount
  useEffect(() => {
    fetchAccounts();
    resetTransactions();
    fetchAllTransactions();
  }, [fetchAccounts, fetchAllTransactions, resetTransactions]);

  // Build current filters object
  const buildFilters = useCallback((): TransactionSearchFilter => {
    const filters: TransactionSearchFilter = {};
    if (searchString) filters.searchString = searchString;
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    if (minAmount) filters.minAmount = parseFloat(minAmount);
    if (maxAmount) filters.maxAmount = parseFloat(maxAmount);
    if (selectedAccountId) filters.accountId = parseInt(selectedAccountId, 10);
    return filters;
  }, [searchString, startDate, endDate, minAmount, maxAmount, selectedAccountId]);

  // The search queries once typing pauses, with the filters as they are then.
  const searchWhenTypingStops = useDebouncedCallback(() => {
    const filters = buildFilters();
    setSearchFilters(filters);
    fetchAllTransactions(filters);
  }, SEARCH_DEBOUNCE_MS);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    searchWhenTypingStops();
  };

  // Handle account filter change
  const handleAccountChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedAccountId(value);
    const filters = buildFilters();
    if (value) {
      filters.accountId = parseInt(value, 10);
    } else {
      delete filters.accountId;
    }
    setSearchFilters(filters);
    await fetchAllTransactions(filters);
  };

  // Handle date filter apply
  const handleDateFilterApply = async () => {
    const filters = buildFilters();
    setSearchFilters(filters);
    await fetchAllTransactions(filters);
    setShowDateFilter(false);
  };

  // Handle date filter clear
  const handleDateFilterClear = async () => {
    setStartDate('');
    setEndDate('');
    const filters = buildFilters();
    delete filters.startDate;
    delete filters.endDate;
    setSearchFilters(filters);
    await fetchAllTransactions(filters);
    setShowDateFilter(false);
  };

  // Handle amount filter apply
  const handleAmountFilterApply = async () => {
    const filters = buildFilters();
    setSearchFilters(filters);
    await fetchAllTransactions(filters);
    setShowAmountFilter(false);
  };

  // Handle amount filter clear
  const handleAmountFilterClear = async () => {
    setMinAmount('');
    setMaxAmount('');
    const filters = buildFilters();
    delete filters.minAmount;
    delete filters.maxAmount;
    setSearchFilters(filters);
    await fetchAllTransactions(filters);
    setShowAmountFilter(false);
  };

  // Clear all filters
  const handleClearAllFilters = async () => {
    setSearchString('');
    setStartDate('');
    setEndDate('');
    setMinAmount('');
    setMaxAmount('');
    setSelectedAccountId('');
    setSearchFilters({});
    await fetchAllTransactions({});
  };

  // Each row's account: the one the page of movements carried (removed accounts included), else
  // the loaded account list. Label first, the bank's name when the owner left none.
  const rowAccounts = useMemo(() => {
    const byId = new Map<number, RowAccount>();
    const add = (a: { id: number; friendlyName: string; bankName: string; bankCode: string; integrationType: string; iban: string; bankLogoUrl: string }) =>
      byId.set(a.id, {
        label: a.friendlyName?.trim() || a.bankName || UNKNOWN_ACCOUNT.label,
        bankName: a.bankName,
        ...logoFor(a),
      });
    accounts.forEach(add);
    Object.values(transactionAccounts).forEach(add);
    return byId;
  }, [accounts, transactionAccounts, logoFor]);

  const handleLoadMore = useCallback(() => {
    loadMoreTransactions();
  }, [loadMoreTransactions]);

  // Check if any filters are active
  const hasActiveFilters = searchString || startDate || endDate || minAmount || maxAmount || selectedAccountId;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">All Transactions</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                View and filter transactions across all your bank accounts.
              </p>
            </div>
            {totalCount > 0 && (
              <div className="text-right">
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Transactions</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{totalCount.toLocaleString('en-US')}</p>
              </div>
            )}
          </div>
        </header>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="error" className="text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              <Icon name="close" />
            </button>
          </div>
        )}

        {/* Transactions Section */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
          {/* Filters Header */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 relative z-30">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <input
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 h-10 pl-10 pr-4 text-sm focus:ring-primary focus:border-primary dark:text-slate-200"
                  placeholder="Search transactions..."
                  type="text"
                  value={searchString}
                  onChange={handleSearch}
                />
                <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              </div>

              {/* Bank Account Selector */}
              <div className="relative min-w-[180px]">
                <select
                  value={selectedAccountId}
                  onChange={handleAccountChange}
                  className={`w-full rounded-lg h-10 px-4 text-sm font-medium border transition-colors appearance-none cursor-pointer pr-10 ${
                    selectedAccountId
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:border-primary'
                  }`}
                >
                  <option value="">All Banks</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.bankName} - {account.friendlyName}
                    </option>
                  ))}
                </select>
                <Icon name="expand_more" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 dark:text-slate-500" />
              </div>

              {/* Date Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowDateFilter(!showDateFilter);
                    setShowAmountFilter(false);
                  }}
                  className={`flex items-center gap-2 rounded-lg h-10 px-4 text-sm font-medium border transition-colors ${
                    startDate || endDate
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:border-primary'
                  }`}
                >
                  <Icon name="calendar_today" />
                  <span>Date</span>
                  {(startDate || endDate) && <span className="w-2 h-2 rounded-full bg-white"></span>}
                </button>
                {showDateFilter && (
                  <div className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg p-4 z-40">
                    <div className="flex flex-col gap-4">
                      <h3 className="font-semibold text-slate-900 dark:text-white">Date Range</h3>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-400" htmlFor="start-date">
                          Start Date
                        </label>
                        <input
                          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                          id="start-date"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-400" htmlFor="end-date">
                          End Date
                        </label>
                        <input
                          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                          id="end-date"
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <button
                          onClick={handleDateFilterClear}
                          className="rounded-md h-9 px-4 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleDateFilterApply}
                          className="rounded-md h-9 px-4 text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Amount Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowAmountFilter(!showAmountFilter);
                    setShowDateFilter(false);
                  }}
                  className={`flex items-center gap-2 rounded-lg h-10 px-4 text-sm font-medium border transition-colors ${
                    minAmount || maxAmount
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:border-primary'
                  }`}
                >
                  <Icon name="payments" />
                  <span>Amount</span>
                  {(minAmount || maxAmount) && <span className="w-2 h-2 rounded-full bg-white"></span>}
                </button>
                {showAmountFilter && (
                  <div className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg p-4 z-40">
                    <div className="flex flex-col gap-4">
                      <h3 className="font-semibold text-slate-900 dark:text-white">Amount Range</h3>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="text-sm font-medium text-slate-600 dark:text-slate-400" htmlFor="min-amount">
                            Min
                          </label>
                          <input
                            className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                            id="min-amount"
                            placeholder="0"
                            type="number"
                            value={minAmount}
                            onChange={(e) => setMinAmount(e.target.value)}
                          />
                        </div>
                        <span className="text-slate-400 mt-5">-</span>
                        <div className="flex-1">
                          <label className="text-sm font-medium text-slate-600 dark:text-slate-400" htmlFor="max-amount">
                            Max
                          </label>
                          <input
                            className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                            id="max-amount"
                            placeholder="10000"
                            type="number"
                            value={maxAmount}
                            onChange={(e) => setMaxAmount(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <button
                          onClick={handleAmountFilterClear}
                          className="rounded-md h-9 px-4 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleAmountFilterApply}
                          className="rounded-md h-9 px-4 text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Clear All Filters */}
              {hasActiveFilters && (
                <button
                  onClick={handleClearAllFilters}
                  className="flex items-center gap-1 rounded-lg h-10 px-3 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <Icon name="filter_alt_off" />
                  <span>Clear</span>
                </button>
              )}
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500 dark:text-slate-400">Active filters:</span>
                {selectedAccountId && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                    Account: {rowAccounts.get(parseInt(selectedAccountId, 10))?.label || 'Selected'}
                  </span>
                )}
                {(startDate || endDate) && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                    Date: {startDate || 'Any'} - {endDate || 'Any'}
                  </span>
                )}
                {(minAmount || maxAmount) && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                    Amount: {minAmount || '0'} - {maxAmount || 'Any'}
                  </span>
                )}
                {searchString && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                    Search: "{searchString}"
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Transactions Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="hidden sm:table-cell px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Account
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Description
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Date
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Amount
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center gap-3 text-slate-500 dark:text-slate-400">
                        <Icon name="progress_activity" className="animate-spin" />
                        Loading transactions...
                      </div>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="receipt_long" size={48} className="text-slate-300 dark:text-slate-600" />
                        <p className="text-slate-500 dark:text-slate-400">
                          {hasActiveFilters
                            ? 'No transactions match your filters.'
                            : 'No transactions found. Connect a bank account to start tracking.'}
                        </p>
                        {!hasActiveFilters && (
                          <button
                            onClick={() => navigate('/accounts/add')}
                            className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-4 py-2 rounded-lg shadow-sm hover:bg-primary/90 transition-all mt-2"
                          >
                            <Icon name="add" />
                            Add Account
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <TransactionRow
                      key={transaction.id}
                      transaction={transaction}
                      account={rowAccounts.get(transaction.bankAccountId) ?? UNKNOWN_ACCOUNT}
                      onSelect={setSelectedTransaction}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Infinite scroll: next page, loading, end of list */}
          {!isLoading && (
            <TransactionListFooter
              loadedCount={transactions.length}
              totalCount={totalCount}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              loadMoreError={loadMoreError}
              onLoadMore={handleLoadMore}
            />
          )}
        </div>
      </div>

      {/* Click outside to close filters */}
      {(showDateFilter || showAmountFilter) && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => {
            setShowDateFilter(false);
            setShowAmountFilter(false);
          }}
        />
      )}

      {/* Transaction Detail Panel */}
      {selectedTransaction && (
        <TransactionDetailPanel transaction={selectedTransaction} onClose={() => setSelectedTransaction(null)} />
      )}
    </div>
  );
};
